/**
 * Mesure d'audience GA4 (lot « GA4 propre », 16/10). Plan de marquage complet :
 * kadelio-notes/specs/plan-de-marquage-ga4.md.
 *
 * Tout passe par `trackEvent`, qui :
 * - n'envoie rien sans l'accord « mesure d'audience » du bandeau cookies, ni
 *   sans gtag charge (rien n'est mis en file d'attente, sauf les evenements du
 *   chargement de page passes par trackEventAfterConsent, rejoues apres
 *   « Accepter » et oublies apres un refus) ;
 * - retire les parametres interdits (nom, e-mail, slug, texte cherche...) ;
 * - ajoute les parametres communs connus : `role` (selon l'evenement),
 *   `via` (canal du lien d'arrivee, garde le temps de l'onglet) et convertit
 *   `occasion` (BABY -> naissance...).
 *
 * Noms standard GA4 la ou ils existent : sign_up, login, share, search,
 * select_item, purchase, generate_lead. Noms francais pour le reste
 * (liste_creee, premier_cadeau, reservation, cagnotte_creee, visite_proche,
 * participation_cagnotte...). Plus envoyes : inscription, partage,
 * clic_marchand, recherche_sans_resultat.
 *
 * Les liens partages portent `?via=<canal>` (withVia) : l'apercu social sur
 * /list/<slug> l'ignore, la page publique le lit pour `visite_proche` et
 * pour la provenance de la visite (usePageTracking, parametre `via`).
 */

import { hasAnalyticsConsent, readConsent } from '@/lib/cookieConsent';

export const CANAUX_PARTAGE = [
  'native', 'sms', 'whatsapp', 'email_app', 'email_kadelio', 'facebook', 'messenger', 'x', 'telegram', 'copie', 'qr',
  // Partage des articles du blog (16/10).
  'pinterest', 'linkedin',
  // Anciens liens envoyes avant la distinction messagerie / envoi par Kadélio.
  'email',
] as const;
export type CanalPartage = (typeof CANAUX_PARTAGE)[number];

export type OrigineActivation = 'liste' | 'mes_cagnottes' | 'parametres' | 'partage' | 'tableau_de_bord' | 'creation' | 'assistant' | 'page_cagnotte';

export type AnalyticsEvent =
  // Noms standard GA4.
  | 'sign_up'
  | 'login'
  | 'share'
  | 'search'
  | 'select_item'
  | 'purchase'
  | 'generate_lead'
  // Noms Kadélio, sans equivalent standard.
  | 'liste_creee'
  | 'premier_cadeau'
  | 'visite_proche'
  | 'visite_article_partage'
  | 'reservation'
  | 'participation_cagnotte'
  | 'participation_declaree'
  | 'cagnotte_creee'
  | 'cagnotte_activation_demarree'
  | 'cagnotte_activee'
  | 'guided_started'
  | 'guided_answered'
  | 'guided_skipped'
  | 'guided_selection_added'
  | 'remerciement_envoye'
  | 'cadeau_recu'
  | 'cadeau_libere'
  | 'catalogue_plus'
  | 'idee_ajoutee'
  | 'inscription_avec_idee';

export type AnalyticsRole = 'organisateur' | 'proche' | 'marque';

/** Article d'un evenement e-commerce GA4 (select_item, purchase) : jamais d'identifiant personnel. */
export interface AnalyticsItem {
  item_name?: string;
  item_brand?: string;
  item_category?: string;
  price?: number;
  quantity?: number;
}

type ParamValue = string | number | boolean | undefined;
export type AnalyticsParams = Record<string, ParamValue | AnalyticsItem[]>;

/** Parametres jamais envoyes, meme si un appel les passe (donnees personnelles, texte libre). */
export const FORBIDDEN_PARAMS = ['search_term', 'name', 'nom', 'prenom', 'email', 'e_mail', 'slug', 'title', 'titre', 'message', 'token'] as const;

/** Role par defaut de chaque evenement (un appel peut le preciser, ex. share depuis la liste publique). */
const EVENT_ROLE: Partial<Record<AnalyticsEvent, AnalyticsRole>> = {
  liste_creee: 'organisateur',
  premier_cadeau: 'organisateur',
  cagnotte_creee: 'organisateur',
  cagnotte_activation_demarree: 'organisateur',
  cagnotte_activee: 'organisateur',
  remerciement_envoye: 'organisateur',
  cadeau_recu: 'organisateur',
  cadeau_libere: 'organisateur',
  guided_started: 'organisateur',
  guided_answered: 'organisateur',
  guided_skipped: 'organisateur',
  guided_selection_added: 'organisateur',
  share: 'organisateur',
  visite_proche: 'proche',
  reservation: 'proche',
  participation_cagnotte: 'proche',
  participation_declaree: 'proche',
  purchase: 'marque',
  generate_lead: 'marque',
};

const OCCASIONS: Record<string, string> = { BABY: 'naissance', WEDDING: 'mariage', BIRTHDAY: 'anniversaire', CHRISTMAS: 'noel', OTHER: 'autre' };

/** Occasion au format du plan de marquage (naissance, mariage, anniversaire, noel, autre...). */
export function analyticsOccasion(occasion: string | null | undefined): string | undefined {
  if (!occasion) return undefined;
  return OCCASIONS[occasion] ?? occasion.toLowerCase();
}

const VIA_KEY = 'kadelio_via';

/** Retient le canal du lien d'arrivee (?via=) le temps de l'onglet. Appele seulement avec accord. */
export function rememberVia(search: string): void {
  if (!hasAnalyticsConsent()) return;
  const via = readVia(search);
  if (!via) return;
  try { sessionStorage.setItem(VIA_KEY, via); } catch { /* stockage indisponible */ }
}

function currentVia(): string | undefined {
  try { return sessionStorage.getItem(VIA_KEY) ?? undefined; } catch { return undefined; }
}

/**
 * Accord « mesure d'audience » en vigueur, lu comme CookieConsentContext le
 * stocke (format v2, valable 6 mois, migration des choix v1).
 */
export { hasAnalyticsConsent };

/** Parametres prets a envoyer : interdits retires, communs ajoutes, occasion convertie. */
export function cleanParams(name: AnalyticsEvent, props: AnalyticsParams = {}): Record<string, string | number | boolean | AnalyticsItem[]> {
  const clean: Record<string, string | number | boolean | AnalyticsItem[]> = {};
  for (const [k, v] of Object.entries(props)) {
    if (v === undefined || (FORBIDDEN_PARAMS as readonly string[]).includes(k)) continue;
    clean[k] = v as string | number | boolean | AnalyticsItem[];
  }
  if (typeof clean.occasion === 'string') clean.occasion = analyticsOccasion(clean.occasion)!;
  // Partage d'un article de blog : lecteur quelconque, pas de role.
  const roleless = name === 'share' && clean.content_type === 'article';
  if (clean.role === undefined && EVENT_ROLE[name] && !roleless) clean.role = EVENT_ROLE[name]!;
  if (clean.via === undefined) {
    const via = currentVia();
    if (via) clean.via = via;
  }
  return clean;
}

/**
 * Envoie un evenement. Renvoie true s'il est parti (utile aux tests), false
 * sans accord ou sans gtag charge.
 */
export function trackEvent(name: AnalyticsEvent, props: AnalyticsParams = {}): boolean {
  if (typeof window === 'undefined' || !hasAnalyticsConsent()) return false;
  if (typeof window.gtag !== 'function') return false;
  try {
    window.gtag('event', name, cleanParams(name, props));
    return true;
  } catch {
    return false;
  }
}

// ---------- Evenements du chargement de page, rejoues apres l'accord ----------

/**
 * Evenements emis au chargement d'une page (visite_proche, visite_article_partage)
 * pendant que le bandeau cookies attend un choix (R-11) : ils sont gardes en
 * memoire, dans cet onglet seulement, puis envoyes si la personne accepte, et
 * oublies si elle refuse. Rien ne part et rien n'est stocke avant l'accord.
 */
const DEFERRED_MAX = 10;
let deferred: { name: AnalyticsEvent; props: AnalyticsParams }[] = [];

/**
 * Comme trackEvent, mais un evenement emis sans choix enregistre est garde
 * pour etre rejoue apres « Accepter ». Apres un refus, il est perdu.
 */
export function trackEventAfterConsent(name: AnalyticsEvent, props: AnalyticsParams = {}): boolean {
  if (typeof window === 'undefined') return false;
  if (hasAnalyticsConsent()) return trackEvent(name, props);
  if (readConsent() === null && deferred.length < DEFERRED_MAX) deferred.push({ name, props: { ...props } });
  return false;
}

/** Apres l'accord : envoie les evenements gardes, dans l'ordre. Renvoie le nombre envoye. */
export function flushDeferredEvents(): number {
  if (!hasAnalyticsConsent()) return 0;
  const pending = deferred;
  deferred = [];
  return pending.filter(({ name, props }) => trackEvent(name, props)).length;
}

/** Refus (ou session admin) : les evenements gardes sont oublies sans envoi. */
export function clearDeferredEvents(): void {
  deferred = [];
}

/** Nombre d'evenements en attente d'un choix (tests). */
export function deferredEventCount(): number {
  return deferred.length;
}

/** `share` (standard GA4) : `method` = canal, `content_type` = liste | cagnotte | article. */
export function trackShare(method: CanalPartage, contentType: 'liste' | 'cagnotte' | 'article', lieu?: string, extra: AnalyticsParams = {}): boolean {
  // `extra` : precisions sans donnee personnelle (ex. position du bouton sur un article).
  return trackEvent('share', { ...extra, method, content_type: contentType, lieu, ...(lieu === 'proche' ? { role: 'proche' } : {}) });
}

// ---------- Paiements : evenements envoyes au retour de Stripe ----------

const PENDING_CONTRIBUTION_KEY = 'kadelio_participation_en_cours';
const SENT_TRANSACTIONS_KEY = 'kadelio_transactions_mesurees';

/** Avant la redirection vers Stripe : montant choisi, pour `participation_cagnotte` au retour. */
export function rememberPendingContribution(amountCents: number, type: 'liste' | 'seule'): void {
  if (!hasAnalyticsConsent()) return;
  try { sessionStorage.setItem(PENDING_CONTRIBUTION_KEY, JSON.stringify({ amountCents, type })); } catch { /* stockage indisponible */ }
}

/** Paiement annule : le montant garde est oublie. */
export function clearPendingContribution(): void {
  try { sessionStorage.removeItem(PENDING_CONTRIBUTION_KEY); } catch { /* stockage indisponible */ }
}

/** Retour de paiement reussi d'une participation : envoie `participation_cagnotte` une seule fois. */
export function trackContributionReturn(): boolean {
  let pending: { amountCents?: unknown; type?: unknown } | null;
  try {
    const raw = sessionStorage.getItem(PENDING_CONTRIBUTION_KEY);
    sessionStorage.removeItem(PENDING_CONTRIBUTION_KEY);
    pending = raw ? JSON.parse(raw) : null;
  } catch { pending = null; }
  if (!pending || typeof pending.amountCents !== 'number' || pending.amountCents <= 0) return false;
  return trackEvent('participation_cagnotte', {
    value: pending.amountCents / 100,
    currency: 'EUR',
    type: pending.type === 'seule' ? 'seule' : 'liste',
    methode: 'carte',
  });
}

/** Moyen choisi pour une participation en mode manuel (cagnotte de liste). */
export type MoyenParticipationManuelle = 'rib' | 'paypal' | 'wero' | 'cash' | 'check';

/**
 * Participation declaree en mode manuel (virement, PayPal, Wero, en main
 * propre, cheque), enregistree avec succes : `participation_declaree` avec
 * `methode: manuelle` (R-13). `value` est le montant declare, pas un paiement
 * constate : les rapports de revenus filtrent sur `methode` (plan de marquage).
 * Jamais le nom, l'e-mail ni le message du proche.
 */
export function trackManualContribution(amountEuros: number, moyen: MoyenParticipationManuelle): boolean {
  if (!Number.isFinite(amountEuros) || amountEuros <= 0) return false;
  // Evenement distinct de participation_cagnotte (evenement cle, paiements
  // constates) : une declaration ne doit pas gonfler les conversions.
  return trackEvent('participation_declaree', {
    value: Math.round(amountEuros * 100) / 100,
    currency: 'EUR',
    type: 'liste',
    methode: 'manuelle',
    moyen,
  });
}

function alreadySent(id: string): boolean {
  try {
    const list = JSON.parse(localStorage.getItem(SENT_TRANSACTIONS_KEY) ?? '[]') as string[];
    return Array.isArray(list) && list.includes(id);
  } catch { return false; }
}

function markSent(id: string): void {
  try {
    const list = JSON.parse(localStorage.getItem(SENT_TRANSACTIONS_KEY) ?? '[]') as string[];
    localStorage.setItem(SENT_TRANSACTIONS_KEY, JSON.stringify([...(Array.isArray(list) ? list : []), id].slice(-20)));
  } catch { /* stockage indisponible */ }
}

const OPTION_NAMES: Record<string, string> = { VITRINE: 'Vitrine', INSPIRATION: 'Inspiration', PACK_NOEL: 'Pack Noël' };

/**
 * Retour de paiement d'une option achetee par une marque (?option=success) :
 * `purchase` avec l'identifiant Stripe de la session, une seule fois par
 * transaction (rechargement ou retour arriere sans doublon).
 */
export function trackOptionPurchaseReturn(params: URLSearchParams): boolean {
  if (params.get('option') !== 'success') return false;
  const transactionId = params.get('session_id');
  const cents = Number(params.get('montant'));
  const kind = params.get('formule') ?? '';
  if (!transactionId || !/^cs_[A-Za-z0-9_]+$/.test(transactionId) || !Number.isFinite(cents) || cents < 0) return false;
  if (alreadySent(transactionId)) return false;
  const sent = trackEvent('purchase', {
    transaction_id: transactionId,
    value: cents / 100,
    currency: 'EUR',
    items: [{ item_name: OPTION_NAMES[kind] ?? 'Option', item_category: 'option_partenaire', price: cents / 100, quantity: 1 }],
  });
  if (sent) markSent(transactionId);
  return sent;
}

const SIGNUP_SOURCE_KEY = 'kadelio_inscription_source';

/** Provenance de l'inscription gardee pendant l'aller-retour Google ou Facebook. */
export function rememberSignupSource(source: string): void {
  try { sessionStorage.setItem(SIGNUP_SOURCE_KEY, source); } catch { /* stockage indisponible */ }
}

/** Provenance gardee avant Google ou Facebook, lue une seule fois. */
export function takeSignupSource(): string | null {
  try {
    const v = sessionStorage.getItem(SIGNUP_SOURCE_KEY);
    sessionStorage.removeItem(SIGNUP_SOURCE_KEY);
    return v;
  } catch {
    return null;
  }
}

/** Ajoute `?via=<canal>` a un lien partage, sans toucher au reste de l'URL. */
export function withVia(url: string, canal: CanalPartage): string {
  try {
    const u = new URL(url);
    u.searchParams.set('via', canal);
    return u.toString();
  } catch {
    return url;
  }
}

/** Canal lu dans `?via=` (inconnu ou absent : null). */
export function readVia(search: string): CanalPartage | null {
  const v = new URLSearchParams(search).get('via');
  return v && (CANAUX_PARTAGE as readonly string[]).includes(v) ? (v as CanalPartage) : null;
}

/**
 * Chemin envoye a la mesure d'audience (/api/track, Google Analytics) : le
 * jeton du lien « Ma réservation » (lot C1) n'en sort jamais.
 */
export function analyticsPath(pathname: string): string {
  return pathname.replace(/^\/reservation\/[^/]+/, '/reservation').replace(/^\/invitation\/[^/]+/, '/invitation');
}

/**
 * Chemin et titre envoyes a Google Analytics : ceux d'une liste publique ou
 * d'une cagnotte sont generiques (le slug et le titre contiennent souvent des
 * prenoms). /api/track garde le chemin d'analyticsPath (statistiques internes).
 */
export function gaPage(pathname: string): { path: string; title?: string } {
  const path = analyticsPath(pathname);
  if (/^\/list\/[^/]+/.test(path)) return { path: '/list', title: 'Liste publique | Kadélio' };
  if (/^\/cagnotte\/(?!creer$)[^/]+$/.test(path)) return { path: '/cagnotte', title: 'Cagnotte | Kadélio' };
  return { path };
}

/**
 * Pages dont la requete ne part pas non plus : ?annuler=1 de « Ma réservation »,
 * jetons de reinitialisation, de verification et de creation du mot de passe,
 * jeton de session du retour Google / Facebook, adresse e-mail de
 * /verify-email/sent. Ni jeton ni e-mail ne doivent partir chez Google.
 */
const PRIVATE_PATH_RE = /^\/(reservation|invitation|reset-password|verify-email|setup-password|oauth-callback)(\/|$)/;

export function isPrivatePath(pathname: string): boolean {
  return PRIVATE_PATH_RE.test(pathname);
}

/**
 * Remplace le jeton dans une adresse (page ou API) : rapports d'erreur,
 * journaux. Lien « Ma réservation » et lien d'invitation a co-organiser.
 */
export function scrubReservationToken(url: string): string {
  return url
    .replace(/(\/api\/public\/reservations\/)[^/?#]+/g, '$1[jeton]')
    .replace(/(\/reservation\/)[^/?#]+/g, '$1[jeton]')
    .replace(/(\/api\/invitations\/)[^/?#]+/g, '$1[jeton]')
    .replace(/(\/invitation\/)[^/?#]+/g, '$1[jeton]');
}

/**
 * Appel dont l'adresse porte un jeton : aucune trace de performance n'est
 * creee pour lui (regle Sentry : un secret ne part jamais dans une trace).
 */
export function isSecretRequestUrl(url: string): boolean {
  return /\/api\/(public\/reservations|invitations)\/[^/?#]+/.test(url);
}
